import { C_CORP_TAX_ENTITY } from '@/features/user/signup/helpers'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { NumberOfEmployees } from '@/features/user/signup/your-practice/index'

/**
 * The practice answers qualification is decided from. Member types are deliberately
 * loose, matching the form values the signup flow already passes: the predicates below
 * compare via `?.toString()`, so tightening these would change verdicts.
 */
export type PracticeAnswers = {
  taxEntityType: TAX_ENTITY_TYPES | 'other'
  businessEntity?: string
  hasMultipleOwners: boolean | string | null
  hasPhysicalInventory: boolean | string | null
  fileInternationalTaxes?: boolean | string | null
  scorpEntireYear: boolean | string | null
  numberOfEmployees: string
}

export type DisqualificationReason =
  | 'c_corp'
  | 'multiple_owners'
  | 'international_taxes'
  | 'scorp_partial_year'
  | 'physical_inventory'
  | 'ten_or_more_employees'

/**
 * Four treatments rather than a boolean, because the flow has three destinations and
 * the precedence between them matters: ten-or-more employees and an "other" business
 * entity are evaluated after the hard reasons, and the employee count only contributes
 * a reason when the person is disqualified for something else as well.
 */
export type QualificationVerdict =
  | { treatment: 'qualified' }
  | { treatment: 'disqualified'; reasons: DisqualificationReason[] }
  | { treatment: 'waitlist' }
  | { treatment: 'consult' }

const isAnswered = (value: boolean | string | null | undefined): boolean =>
  value !== null && value !== undefined && value !== ''

/**
 * Whether every answer a verdict depends on has been given.
 *
 * This must be checked *before* `evaluateQualification`. The predicates there treat an
 * unanswered question as failing — `hasMultipleOwners` qualifies only on an explicit
 * `'false'` — so a flow that may not have asked yet would otherwise report an unasked
 * person as disqualified. Incomplete means collect more, never reject.
 */
export const isAnswerSetComplete = (answers: PracticeAnswers): boolean =>
  isAnswered(answers.taxEntityType) &&
  isAnswered(answers.businessEntity) &&
  isAnswered(answers.hasMultipleOwners) &&
  isAnswered(answers.hasPhysicalInventory) &&
  isAnswered(answers.fileInternationalTaxes) &&
  isAnswered(answers.scorpEntireYear) &&
  isAnswered(answers.numberOfEmployees)

export const evaluateQualification = (
  answers: PracticeAnswers
): QualificationVerdict => {
  const {
    taxEntityType,
    businessEntity,
    hasMultipleOwners,
    hasPhysicalInventory,
    fileInternationalTaxes,
    scorpEntireYear,
    numberOfEmployees,
  } = answers

  const isValidTaxEntityType = taxEntityType !== C_CORP_TAX_ENTITY
  const isValidOwnerCount = hasMultipleOwners?.toString() === 'false'
  const isValidTaxNationalities = fileInternationalTaxes?.toString() !== 'true'
  const isValidScorpEntireYear = scorpEntireYear?.toString() !== 'false'
  const hasPhysicalInventoryDq = hasPhysicalInventory?.toString() === 'true'

  const isQualifiedUser =
    isValidTaxEntityType &&
    isValidOwnerCount &&
    isValidTaxNationalities &&
    isValidScorpEntireYear &&
    !hasPhysicalInventoryDq

  const has10OrMoreEmployees = numberOfEmployees === NumberOfEmployees.tenOrMore

  if (!isQualifiedUser) {
    return {
      treatment: 'disqualified',
      reasons: [
        ...(!isValidTaxEntityType ? (['c_corp'] as const) : []),
        ...(!isValidOwnerCount ? (['multiple_owners'] as const) : []),
        ...(!isValidTaxNationalities ? (['international_taxes'] as const) : []),
        ...(!isValidScorpEntireYear ? (['scorp_partial_year'] as const) : []),
        ...(hasPhysicalInventoryDq ? (['physical_inventory'] as const) : []),
        ...(has10OrMoreEmployees ? (['ten_or_more_employees'] as const) : []),
      ],
    }
  }

  if (has10OrMoreEmployees) return { treatment: 'waitlist' }
  if (businessEntity === 'other') return { treatment: 'consult' }
  return { treatment: 'qualified' }
}
